// SB6-ANA-POP-02 — synchronise the pop-out workspace (which widgets live in
// which window + each window's grid) across all open windows.
//
// Last-write-wins over the shared BroadcastChannel: each window broadcasts the
// full workspace on a LOCAL mutation (tagged `origin: 'local'` by the store)
// and adopts an incoming workspace only if its `version` is newer. A window
// that just booted posts `requestWorkspace`; any window holding state answers.
//
// Runs in BOTH the main window and every pop-out window. Mirrors the
// image/selection/prefs sync in `useViewerPopoutSync`, on the same channel.
import { useEffect, useRef } from "react";
import { createSyncChannel, type SyncChannel } from "./popoutSync.js";
import {
  usePopoutWorkspaceStore,
  type PopoutWindowState,
} from "./popoutWorkspaceStore.js";

export interface UseWorkspaceSyncArgs {
  readonly inspectionId: string;
  /** True in a popped-out window. */
  readonly isPopout: boolean;
}

export function useWorkspaceSync({
  inspectionId,
  isPopout,
}: UseWorkspaceSyncArgs): void {
  const version = usePopoutWorkspaceStore((s) => s.version);
  const origin = usePopoutWorkspaceStore((s) => s.origin);
  const windows = usePopoutWorkspaceStore((s) => s.windows);

  // Sync while a window exists, or we ARE a pop-out (so a freshly-opened
  // pop-out can request state even before its own window count settles).
  const enabled = isPopout || windows.length > 0;

  const channelRef = useRef<SyncChannel | null>(null);
  const lastSentRef = useRef<number>(0);

  // Install channel + apply incoming workspace / answer requests.
  useEffect(() => {
    if (!enabled || !inspectionId) return;
    const ch = createSyncChannel({ dataAcquisitionId: inspectionId });
    channelRef.current = ch;
    const off = ch.addListener((msg) => {
      if (msg.type === "workspace") {
        lastSentRef.current = Math.max(lastSentRef.current, msg.version);
        usePopoutWorkspaceStore
          .getState()
          .applyRemote(msg.version, msg.windows as PopoutWindowState[]);
      } else if (msg.type === "requestWorkspace") {
        const s = usePopoutWorkspaceStore.getState();
        // Answer from the window that owns state (main, or any with windows).
        if (!isPopout || s.windows.length > 0) {
          ch.postMessage({
            type: "workspace",
            version: s.version,
            windows: s.windows,
          });
          lastSentRef.current = Math.max(lastSentRef.current, s.version);
        }
      }
    });
    // Ask for the current workspace as soon as we're listening.
    ch.postMessage({ type: "requestWorkspace" });
    return () => {
      off();
      ch.close();
      channelRef.current = null;
    };
  }, [enabled, inspectionId, isPopout]);

  // Broadcast local mutations; never re-broadcast an applied remote one.
  useEffect(() => {
    if (!enabled) return;
    if (origin === "local" && version > lastSentRef.current) {
      channelRef.current?.postMessage({ type: "workspace", version, windows });
      lastSentRef.current = version;
    } else if (origin === "remote") {
      lastSentRef.current = Math.max(lastSentRef.current, version);
    }
  }, [enabled, origin, version, windows]);
}
